import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Router,
  RouterStateSnapshot,
} from '@angular/router';
import { AppRoute } from 'app/app-layout/constants/app-route.enum';
import { Observable, of } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';

import { FeatureCode } from '../models';
import { FeatureControlService } from '../services';

/**
 * Guard to control access to routes based on feature flags.
 * Prevents navigation to routes when their associated feature is inactive.
 */
@Injectable({
  providedIn: 'root',
})
export class FeatureControlGuard implements CanActivate {
  constructor(
    private readonly _featureControlService: FeatureControlService,
    private readonly _router: Router
  ) {}

  public canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> {
    const featureCode: FeatureCode = route.data['featureCode'] as FeatureCode;

    if (!featureCode) {
      // If no feature code is specified, allow access
      return of(true);
    }

    return this._featureControlService.areFeaturesLoaded().pipe(
      filter((loaded: boolean) => loaded),
      take(1),
      switchMap(() => this._featureControlService.isFeatureActive(featureCode)),
      take(1),
      map((isActive: boolean) => {
        if (!isActive) {
          // Redirect to dashboard if feature is not active
          this._router.navigate(['/' + AppRoute.dashboard]);
          return false;
        }
        return true;
      })
    );
  }
}
